import { Decimal } from 'decimal.js';
import { z } from 'zod';
import { baseTotalSchema, currencyBalanceSchema } from './accounts.js';
import { currencyCodeSchema } from './currency.js';
import { debtSchema } from './debts.js';
import { addDays, addMonths, calendarMonthSchema, daysInMonth } from './installments.js';
import { moneyAmountSchema } from './money.js';
import { isoDateSchema } from './transactions.js';
/**
 * Reports (Phase 6, docs/plans/phase-6.md). They only read the ledger: every figure is a SUM of
 * posting base amounts (each posting's own rate snapshot) on transactions the viewer can see.
 * Date ranges are inclusive `YYYY-MM-DD` days of `occurred_on` (already in the family time zone).
 */
/**
 * Whose spending a report shows: `family` = everything the viewer can see; `member` = one
 * member's (needs reports.view); `mine` = the viewer's own (everyone without reports.view).
 * A member's spending = what they recorded or paid from an account they own, and corrections
 * of those (the children's limit rule).
 */
export const REPORT_SCOPES = ['family', 'member', 'mine'];
export const reportScopeSchema = z.enum(REPORT_SCOPES);
const scopeQuery = {
    /** One member's spending (needs reports.view); without it, the viewer's own. */
    memberId: z.string().uuid().optional(),
};
const scopeFields = {
    scope: reportScopeSchema,
    /** The member in scope (`member` and `mine`). */
    memberId: z.string().uuid().nullable(),
    /** The family's base currency: every amount is in it. */
    currency: currencyCodeSchema,
};
/** The most days one custom range may cover (about three years). */
export const MAX_REPORT_RANGE_DAYS = 1100;
export const spendingReportQuerySchema = z
    .object({
    ...scopeQuery,
    month: calendarMonthSchema.optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
})
    .refine((q) => (q.from === undefined) === (q.to === undefined), {
    message: 'Give both from and to, or neither',
})
    .refine((q) => q.from === undefined || q.month === undefined, {
    message: 'Give a month or a range, not both',
})
    .refine((q) => q.from === undefined || q.to === undefined || q.from <= q.to, {
    message: 'from must be on or before to',
});
export const spendingLineSchema = z.object({
    categoryId: z.string().uuid(),
    name: z.string(),
    amount: moneyAmountSchema,
    /** Share of the total, whole percent; null when the total is not positive. */
    percent: z.string().nullable(),
});
export const spendingReportSchema = z.object({
    ...scopeFields,
    from: z.string(),
    to: z.string(),
    total: moneyAmountSchema,
    /** Top-level categories, biggest first, each with its subcategories rolled up into it. */
    categories: z.array(spendingLineSchema.extend({
        /** Spent on the parent itself and on each subcategory (biggest first). */
        own: moneyAmountSchema,
        subcategories: z.array(spendingLineSchema),
    })),
});
export const compareReportQuerySchema = z.object({
    ...scopeQuery,
    month: calendarMonthSchema.optional(),
});
export const comparedAmountSchema = z.object({
    current: moneyAmountSchema,
    previous: moneyAmountSchema,
    /** current − previous (positive = spent more). */
    difference: moneyAmountSchema,
    /** difference / previous, whole percent with sign; null when previous is not positive. */
    percent: z.string().nullable(),
});
export const compareReportSchema = z.object({
    ...scopeFields,
    month: z.string(),
    current: z.object({ from: z.string(), to: z.string() }),
    previous: z.object({ from: z.string(), to: z.string() }),
    /** Month to date (the current month), compared with the same days of last month. */
    monthToDate: z.boolean(),
    overall: comparedAmountSchema,
    /** Top-level categories (subcategories rolled up), biggest increase first. */
    categories: z.array(comparedAmountSchema.extend({ categoryId: z.string().uuid(), name: z.string() })),
});
export const incomeExpenseReportQuerySchema = z.object({
    ...scopeQuery,
    /** The last of the 12 months (default: this month). */
    month: calendarMonthSchema.optional(),
});
export const incomeExpenseMonthSchema = z.object({
    month: z.string(),
    income: moneyAmountSchema,
    expense: moneyAmountSchema,
    /** income − expense: positive = saved, negative = overspent. */
    net: moneyAmountSchema,
    /**
     * Gained (positive) or lost (negative) on exchanges and "paid in another currency" against
     * the rates used: never part of income or expense.
     */
    exchangeDifference: moneyAmountSchema,
});
export const incomeExpenseReportSchema = z.object({
    ...scopeFields,
    /** Oldest first, 12 months ending at the chosen month. */
    months: z.array(incomeExpenseMonthSchema),
    totals: incomeExpenseMonthSchema.omit({ month: true }),
});
export const debtsReportQuerySchema = z.object(scopeQuery);
/** "Export PDF": one month (default: this month), for the family, a member or "mine". */
export const reportPdfQuerySchema = z.object({
    ...scopeQuery,
    month: calendarMonthSchema.optional(),
});
export const debtsReportPersonSchema = z.object({
    /** `member:<id>` or `outside:<id>`. */
    key: z.string(),
    name: z.string(),
    /** A family member: family-wide this cancels out and is left out of the totals. */
    internal: z.boolean(),
    /** Open debts with this person, each in its own currency. */
    debts: z.array(debtSchema),
    byCurrency: z.array(currencyBalanceSchema),
});
export const debtsReportSideSchema = z.object({
    people: z.array(debtsReportPersonSchema),
    byCurrency: z.array(currencyBalanceSchema),
    /** One total in the base currency at today's family rate (labelled so; an estimate). */
    atTodaysRate: baseTotalSchema,
});
export const debtsReportDueSchema = z.object({
    debtId: z.string().uuid(),
    personName: z.string(),
    direction: z.enum(['borrowed', 'lent']),
    /** An installment of a plan, or a credit purchase / credit work with a due date. */
    source: z.enum(['installment', 'credit']),
    /** A credit item's name (its description or payee); null for an installment. */
    name: z.string().nullable().default(null),
    dueOn: z.string(),
    state: z.enum(['upcoming', 'due_today', 'overdue']),
    amount: moneyAmountSchema,
    currency: currencyCodeSchema,
});
export const debtsReportSchema = z.object({
    ...scopeFields,
    today: z.string(),
    owedToUs: debtsReportSideSchema,
    weOwe: debtsReportSideSchema,
    /** Overdue, due today and due within DUE_SOON_DAYS, oldest first. */
    due: z.array(debtsReportDueSchema),
});
/** First and last day (inclusive) of a `YYYY-MM` month. */
export function monthBounds(month) {
    const [year, m] = month.split('-').map((part) => Number.parseInt(part, 10));
    return { from: `${month}-01`, to: `${month}-${String(daysInMonth(year, m)).padStart(2, '0')}` };
}
/**
 * The two periods "this month vs last month" compares. For the current month: month to date
 * (day 1 to today) against the same number of days of last month, cut at the end of a shorter
 * month (Mar 1–31 against Feb 1–28). For a past month: both months in full.
 */
export function compareRanges(month, today) {
    const previousMonth = addMonths(month, -1);
    const current = monthBounds(month);
    const previous = monthBounds(previousMonth);
    if (today.slice(0, 7) !== month)
        return { current, previous, monthToDate: false };
    const days = Number.parseInt(today.slice(8, 10), 10);
    const sameDay = addDays(previous.from, days - 1);
    return {
        current: { from: current.from, to: today },
        previous: { from: previous.from, to: sameDay < previous.to ? sameDay : previous.to },
        monthToDate: true,
    };
}
/**
 * The change from `previous` to `current` in whole percent, with its sign ("+25", "-10", "0");
 * null when there is nothing to compare with (previous not positive).
 */
export function changePercent(current, previous) {
    const before = new Decimal(previous);
    if (before.lte(0))
        return null;
    const value = new Decimal(current)
        .minus(before)
        .dividedBy(before)
        .times(100)
        .toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
    return value.isZero() ? '0' : value.isPositive() ? `+${value.toFixed(0)}` : value.toFixed(0);
}
//# sourceMappingURL=reports.js.map